import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { VideoImportsRequester } from '../Services/VideoImports.requester';
import {
	AddUnitVideoImportPayload,
	CreateVideoImportFolderPayload,
	CreateVideoImportPayload,
	FetchVideoImportsPayload,
	GetVideoImportPayload,
	InspectVideoImportFolderPayload,
	VideoImport,
	VideoImportCapabilities,
} from '../Types/VideoImports.types';

export type VideoImportsState = {
	list: VideoImport[];
	meta: ResponseMeta | null;
	capabilities: VideoImportCapabilities | null;
	loaders: {
		fetchAll?: StateLoader;
		create?: StateLoader;
		addUnit?: StateLoader;
		retry?: StateLoader;
		delete?: StateLoader;
		capabilities?: StateLoader;
	};
	errors: {
		fetchAll?: string | null;
		create?: string | null;
		addUnit?: string | null;
		retry?: string | null;
		delete?: string | null;
		capabilities?: string | null;
	};
};

const initialState: VideoImportsState = {
	list: [],
	meta: null,
	capabilities: null,
	loaders: {},
	errors: {},
};

/** Friendly message off an axios error — backend 422/409s carry the real text. */
export const videoImportErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ??
	error?.response?.data?.errors?.[0]?.message ??
	fallback;

const requester = VideoImportsRequester.getInstance();

export const videoImportsFetchAll = createAsyncThunk(
	'VideoImports/fetchAll',
	async (payload: FetchVideoImportsPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.fetch(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoImportsCreate = createAsyncThunk(
	'VideoImports/create',
	async (payload: CreateVideoImportPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.create(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoImportsCreateFolder = createAsyncThunk(
	'VideoImports/createFolder',
	async (payload: CreateVideoImportFolderPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.createFolder(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

// Chip-only probe: no loader, no slice state — the form keeps its own cache.
export const videoImportsInspectFolder = createAsyncThunk(
	'VideoImports/inspectFolder',
	async (payload: InspectVideoImportFolderPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.inspectFolder(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoImportsAddUnit = createAsyncThunk(
	'VideoImports/addUnit',
	async (payload: AddUnitVideoImportPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.addUnit(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoImportsRetry = createAsyncThunk(
	'VideoImports/retry',
	async (payload: GetVideoImportPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.retry(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoImportsDelete = createAsyncThunk(
	'VideoImports/delete',
	async (payload: GetVideoImportPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.deleteById(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoImportsCapabilities = createAsyncThunk(
	'VideoImports/capabilities',
	async (_: void, { rejectWithValue }) => {
		try {
			const { data } = await requester.providers();
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const videoImportsSlice = createSlice({
	name: 'VideoImports',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(videoImportsFetchAll.pending, (state, action) => {
				// Silent background polls never flip loading state — the table
				// must sit perfectly still unless data actually changed.
				if (action.meta.arg.silent) return;
				state.loaders.fetchAll = 'pending';
				state.errors.fetchAll = null;
			})
			.addCase(videoImportsFetchAll.fulfilled, (state, action) => {
				const { payload } = action;
				// Differential update: keep existing references (no re-render)
				// when nothing changed.
				if (JSON.stringify(state.list) !== JSON.stringify(payload.data.imports)) {
					state.list = payload.data.imports;
				}
				if (JSON.stringify(state.meta) !== JSON.stringify(payload.meta)) {
					state.meta = payload.meta;
				}
				if (!action.meta.arg.silent) state.loaders.fetchAll = 'fulfilled';
			})
			.addCase(videoImportsFetchAll.rejected, (state, action) => {
				if (action.meta.arg.silent) return; // a failed background poll is invisible
				state.loaders.fetchAll = 'rejected';
				state.errors.fetchAll = videoImportErrorMessage(
					action.payload,
					"Couldn't load video imports"
				);
			});
		builder
			.addCase(videoImportsCreate.pending, (state) => {
				state.loaders.create = 'pending';
				state.errors.create = null;
			})
			.addCase(videoImportsCreate.fulfilled, (state) => {
				state.loaders.create = 'fulfilled';
			})
			.addCase(videoImportsCreate.rejected, (state, action) => {
				state.loaders.create = 'rejected';
				state.errors.create = videoImportErrorMessage(
					action.payload,
					"Couldn't start the video import"
				);
			});
		// Folder expansion shares the create loader/error slot — the form
		// treats both as "a submit is in flight".
		builder
			.addCase(videoImportsCreateFolder.pending, (state) => {
				state.loaders.create = 'pending';
				state.errors.create = null;
			})
			.addCase(videoImportsCreateFolder.fulfilled, (state) => {
				state.loaders.create = 'fulfilled';
			})
			.addCase(videoImportsCreateFolder.rejected, (state, action) => {
				state.loaders.create = 'rejected';
				state.errors.create = videoImportErrorMessage(
					action.payload,
					"Couldn't start the folder import"
				);
			});
		builder
			.addCase(videoImportsAddUnit.pending, (state) => {
				state.loaders.addUnit = 'pending';
				state.errors.addUnit = null;
			})
			.addCase(videoImportsAddUnit.fulfilled, (state, { payload }) => {
				const updated = payload.data.import;
				state.list = state.list.map((row) =>
					row.id === updated.id ? updated : row
				);
				state.loaders.addUnit = 'fulfilled';
			})
			.addCase(videoImportsAddUnit.rejected, (state, action) => {
				state.loaders.addUnit = 'rejected';
				state.errors.addUnit = videoImportErrorMessage(
					action.payload,
					"Couldn't add the import as a unit"
				);
			});
		builder
			.addCase(videoImportsRetry.pending, (state) => {
				state.loaders.retry = 'pending';
				state.errors.retry = null;
			})
			.addCase(videoImportsRetry.fulfilled, (state, { payload }) => {
				const updated = payload.data.import;
				state.list = state.list.map((row) =>
					row.id === updated.id ? updated : row
				);
				state.loaders.retry = 'fulfilled';
			})
			.addCase(videoImportsRetry.rejected, (state, action) => {
				state.loaders.retry = 'rejected';
				state.errors.retry = videoImportErrorMessage(
					action.payload,
					"Couldn't retry the video import"
				);
			});
		builder
			.addCase(videoImportsDelete.pending, (state) => {
				state.loaders.delete = 'pending';
				state.errors.delete = null;
			})
			.addCase(videoImportsDelete.fulfilled, (state, { meta: { arg } }) => {
				state.list = state.list.filter((row) => row.id !== arg.id);
				state.loaders.delete = 'fulfilled';
			})
			.addCase(videoImportsDelete.rejected, (state, action) => {
				state.loaders.delete = 'rejected';
				state.errors.delete = videoImportErrorMessage(
					action.payload,
					"Couldn't delete the video import"
				);
			});
		builder
			.addCase(videoImportsCapabilities.pending, (state) => {
				state.loaders.capabilities = 'pending';
				state.errors.capabilities = null;
			})
			.addCase(videoImportsCapabilities.fulfilled, (state, { payload }) => {
				state.capabilities = payload.data.providers;
				state.loaders.capabilities = 'fulfilled';
			})
			.addCase(videoImportsCapabilities.rejected, (state, action) => {
				state.loaders.capabilities = 'rejected';
				state.errors.capabilities = videoImportErrorMessage(
					action.payload,
					"Couldn't load provider capabilities"
				);
			});
	},
});

export default videoImportsSlice.reducer;
